import { useState } from "react";

export class SpellCheckerServerError extends Error {
  constructor(message) {
    super(message);
    this.name = "SpellCheckerServerError";
  }
}

export const useSpellChecker = () => {
  const [errors, setErrors] = useState({});
  const [ignoredWords, setIgnoredWords] = useState({});

  const query = async text => {
    const response = await fetch(
      `https://speller.yandex.net/services/spellservice.json/checkText?text=${text}`
    );

    if (response.status >= 500) {
      throw new SpellCheckerServerError("Spell checker server error");
    }

    return await response.json();
  };

  const check = fields => {
    return Promise.all(
      fields.filter(filterByHasValue).map(field => query(field.value))
    ).then(result => {
      if (result.flat().length === 0) {
        return {
          isValid: true
        };
      }

      const errors = getErrors({ result, fields, ignoredWords });

      setErrors(errors);

      return {
        isValid: isValid(errors)
      };
    });
  };

  const getFieldErrors = field => {
    if (Object.keys(errors).length === 0) {
      return undefined;
    }

    return errors[field];
  };

  const ignoreWord = (field, word) =>
    setIgnoredWords(prevState => ({
      ...prevState,
      [field]: prevState[field] ? [...prevState[field], word] : [word]
    }));

  const getFieldProps = field => ({
    errors: getFieldErrors(field),
    ignoreWord,
    check
  });

  const skipSpellErrorsConfirm = callback => {
    const skipSpellErrors = confirm(
      "В форме есть орфографическая ошибки. Всё равно сохранить?"
    );

    if (skipSpellErrors) {
      callback();
    }
  };

  return {
    getFieldProps,
    check,
    skipSpellErrorsConfirm
  };
};

/* Utils */
const ERROR_UNKNOWN_WORD = 1;
const ERROR_REPEAT_WORD = 2;
const ERROR_CAPITALIZATION = 3;
const ERROR_TOO_MANY_ERRORS = 4;

const mapErrorCodeToErrorTitle = {
  [ERROR_UNKNOWN_WORD]: "Орфографическая ошибка",
  [ERROR_REPEAT_WORD]: "Стилистическая ошибка",
  [ERROR_CAPITALIZATION]: "Грамматическая ошибка",
  [ERROR_TOO_MANY_ERRORS]:
    "Текст содержит слишком много ошибок. При этом приложение может отправить Яндекс.Спеллеру оставшийся непроверенным текст в следующем запросе."
};

const mapErrorCodeToErrorDescription = {
  [ERROR_UNKNOWN_WORD]: "Слова нет в словаре",
  [ERROR_REPEAT_WORD]: "Повтор слова",
  [ERROR_CAPITALIZATION]: "Неверное употребление прописных и строчных букв",
  [ERROR_TOO_MANY_ERRORS]:
    "Текст содержит слишком много ошибок. При этом приложение может отправить Яндекс.Спеллеру оставшийся непроверенным текст в следующем запросе."
};

const mapErrorCodeToErrorTag = {
  [ERROR_UNKNOWN_WORD]: "unknown-word",
  [ERROR_REPEAT_WORD]: "repeat-word",
  [ERROR_CAPITALIZATION]: "capitalization",
  [ERROR_TOO_MANY_ERRORS]: ""
};

const filterByHasValue = field =>
  field.value !== undefined && field.value !== null && field.value !== "";

const isValid = errors =>
  Object.keys(errors).reduce((prev, next) => {
    const fieldErrors = errors[next];

    if (fieldErrors && fieldErrors.length !== 0) {
      prev.push(fieldErrors);
    }

    return prev;
  }, []).length === 0;

const getErrors = ({ result, fields, ignoredWords }) => {
  const errors = {};

  for (let i = 0; i < result.length; i++) {
    const field = fields[i].name;
    const fieldIgnoredWords = ignoredWords[field];
    const fieldErrors = result[i];
    const filteredFieldErrors = fieldIgnoredWords
      ? fieldErrors.filter(error => !fieldIgnoredWords.includes(error.word))
      : fieldErrors;

    errors[field] = parseResult(filteredFieldErrors);
  }

  return errors;
};

const parseResult = result =>
  result.map(error => ({
    ...error,
    ui: {
      title: mapErrorCodeToErrorTitle[error.code],
      description: mapErrorCodeToErrorDescription[error.code],
      tag: mapErrorCodeToErrorTag[error.code]
    }
  }));
